/**
 * Minimal dependency-free toast that can be triggered from anywhere — including
 * Redux middleware, where the per-component `useSnackBar` hook (MUI) is not
 * available and the app has no global snackbar provider.
 *
 * It appends a self-removing element to `document.body`. Styling uses Tailwind
 * semantic tokens so the classes are picked up by the Tailwind scanner.
 */

const CONTAINER_ID = "global-toast-container";

/** Toast severity. Defaults to "error" so existing callers (and the error middleware) are unchanged. */
export type ToastVariant = "error" | "success";

const VARIANT_BG: Record<ToastVariant, string> = {
	error: "bg-status-error",
	success: "bg-status-success",
};

const ensureContainer = (): HTMLElement => {
	let container = document.getElementById(CONTAINER_ID);

	if (!container) {
		container = document.createElement("div");
		container.id = CONTAINER_ID;
		container.className =
			"fixed top-4 left-1/2 -translate-x-1/2 z-[2000] flex flex-col items-center gap-2";
		document.body.appendChild(container);
	}

	return container;
};

export const showGlobalToast = (
	message: string,
	durationMs = 6000,
	variant: ToastVariant = "error"
): void => {
	if (typeof document === "undefined") {
		return;
	}

	const toast = document.createElement("div");

	toast.setAttribute("role", "alert");
	toast.className = `max-w-md rounded-md ${VARIANT_BG[variant]} px-4 py-3 text-sm text-white shadow-md`;
	toast.textContent = message;

	const container = ensureContainer();

	container.appendChild(toast);

	window.setTimeout(() => {
		toast.remove();

		if (container.childElementCount === 0) {
			container.remove();
		}
	}, durationMs);
};
